import { Component, h } from 'preact'
import PropTypes from 'prop-types'
import { DataDog } from '../../datadog'
import ManualCardForm from './ManualCardForm'

/**
 * Error boundary that wraps the Evervault card form. When the Evervault
 * SDK/iframe fails to mount or render, it catches the error, logs it to
 * DataDog, and degrades gracefully to the manual card form so checkout
 * stays functional
 *
 * Usage: the CardForm switch wraps `EvervaultCardForm` with this
 * boundary and passes the identical CardForm prop set. On the normal path the
 * boundary renders `children` (the Evervault form); on failure it forwards the
 * same props down to `<ManualCardForm {...props} />`.
 *
 * Security: only error/exception info is logged. Card data
 * (`cardFormValues`/`cardEncryptedData`) is never passed to the logger.
 */
class EvervaultErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error, info) {
    // Log only the error/exception info — never card data.
    DataDog.error(
      'Evervault card form failed to render, falling back to manual card form',
      {
        error: error?.message || String(error),
        stack: error?.stack,
        componentStack: info?.componentStack,
      },
    )

    // Mark Evervault as unavailable so the whole page (form + Pay button +
    // saga) drops to the next form type: PCI iframe if enabled, else default.
    this.props.onError?.(error)
  }

  render() {
    const { children, onError, ...rest } = this.props
    const { hasError } = this.state

    if (hasError) {
      // Forward the CardForm prop set to the fallback so the manual form
      // renders with the same props and checkout remains functional.
      return <ManualCardForm {...rest} />
    }

    return children
  }
}

EvervaultErrorBoundary.propTypes = {
  children: PropTypes.node,
  onError: PropTypes.func,
}

export default EvervaultErrorBoundary
